import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";

/** ------------------ CONSTANTS ------------------ **/

export const FILE_UPLOAD_ERROR = {
  FILE_INVALID_TYPE: "file-invalid-type",
  FILE_TOO_LARGE: "file-too-large",
  TOO_MANY_FILES: "too-many-files",
  HEIC_CONVERSION_ERROR: "heic-conversion-error",
};

export const MAX_NUMBER_OF_FILES_ALLOWED = 10;
export const MAX_SIZE_OF_FILE_ALLOWED_MB = 10; // 10 MB

/** ------------------ UTILITIES ------------------ **/

export const getId = (): string =>
  Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

export const getMBs = (bytes: number): { value: string; type: string } => {
  if (bytes < 1024) return { value: bytes.toString(), type: "bytes" };
  const kb = bytes / 1024;
  if (kb < 1024) return { value: kb.toFixed(1), type: "KB" };
  const mb = kb / 1024;
  if (mb < 1024) return { value: mb.toFixed(1), type: "MB" };
  const gb = mb / 1024;
  return { value: gb.toFixed(1), type: "GB" };
};

/** ------------------ TYPE DEFINITIONS ------------------ **/

export interface UploadedFile extends File {
  uuid: string;
  preview: string;
}

export interface RejectedFileWithPreview {
  file: File;
  preview: string;
  readableErrors: string[];
  errors: { code: string; message: string }[];
}

export interface UseFileUploadOptions {
  accept?: Record<string, string[]>;
  maxSize?: number;
  maxFiles?: number;
  multiple?: boolean;
  isDisabled?: boolean;
  replaceAfterUpload?: boolean;
  files?: UploadedFile[];
  name?: string;
  insideForm?: boolean;
  handleFileChange?: (value: any) => void;
}

export interface UseFileUploadReturn {
  getRootProps: any;
  getInputProps: any;
  isDragActive: boolean;
  isFileDialogOpen: boolean;
  rejectedFiles: RejectedFileWithPreview[];
  openFileDialog: () => void;
  removeFile: (uuid: string) => void;
  clearRejectedFiles: () => void;
}

/** ------------------ MAIN HOOK ------------------ **/

export const useFileUpload = ({
  accept = {
    "image/*": [".png", ".gif", ".jpeg", ".jpg"],
    "application/pdf": [".pdf"],
  },
  maxSize = MAX_SIZE_OF_FILE_ALLOWED_MB * 1024 * 1024,
  maxFiles = MAX_NUMBER_OF_FILES_ALLOWED,
  multiple = true,
  isDisabled = false,
  replaceAfterUpload = false,
  files = [],
  handleFileChange = () => {},
}: UseFileUploadOptions): UseFileUploadReturn => {
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  const [fileUploaded, setFileUploaded] = useState<UploadedFile[]>(files ?? []);
  const [rejectedFiles, setRejectedFile] = useState<RejectedFileWithPreview[]>(
    []
  );

  /** User-friendly error text generator */
  const getErrorText = (errorCode: string): string => {
    switch (errorCode) {
      case FILE_UPLOAD_ERROR.FILE_INVALID_TYPE:
        return "Invalid file type. Please upload a supported format.";
      case FILE_UPLOAD_ERROR.FILE_TOO_LARGE: {
        const { value, type } = getMBs(maxSize);
        return `File too large. Maximum allowed size is ${value} ${type}.`;
      }
      case FILE_UPLOAD_ERROR.TOO_MANY_FILES:
        return `Too many files selected. Maximum allowed: ${maxFiles}.`;
      case FILE_UPLOAD_ERROR.HEIC_CONVERSION_ERROR:
        return "Failed to convert HEIC image. Please upload a JPEG or PNG instead.";
      default:
        return "An unknown file error occurred.";
    }
  };

  /** Update form or callback on change */
  useEffect(() => {
    handleFileChange(fileUploaded);
  }, [fileUploaded]);

  /** Setup Dropzone */
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept,
    maxSize,
    multiple,
    maxFiles,
    disabled: isDisabled,
    noClick: true,
    onFileDialogCancel: () => setIsFileDialogOpen(false),
    onFileDialogOpen: () => {
      if (!isDisabled) setIsFileDialogOpen(true);
    },
    onDrop: async (_acceptedFiles, fileRejections) => {
      const acceptedFiles = await Promise.all(
        _acceptedFiles.map(async (file) => {
          if (!file) return null;

          // Handle HEIC conversion
          if (file.type === "image/heic") {
            try {
              // Dynamically fetch the 1.3MB library ONLY when an HEIC file is uploaded!
              const heic2any = (await import("heic2any")).default;
              const blob = await heic2any({ blob: file, toType: "image/jpeg" });
              const blobArray = Array.isArray(blob) ? blob[0] : blob;
              const convertedFile = new File(
                [blobArray],
                `${file.name.replace(/\.heic$/i, "")}.jpeg`,
                { type: "image/jpeg" }
              );
              return Object.assign(convertedFile, {
                uuid: getId(),
                preview: URL.createObjectURL(convertedFile),
              });
            } catch (err) {
              console.error("HEIC conversion error:", err);
              const msg = getErrorText(FILE_UPLOAD_ERROR.HEIC_CONVERSION_ERROR);
              setRejectedFile((prev) => [
                ...prev,
                {
                  file,
                  preview: URL.createObjectURL(file),
                  readableErrors: [msg],
                  errors: [
                    {
                      code: FILE_UPLOAD_ERROR.HEIC_CONVERSION_ERROR,
                      message: msg,
                    },
                  ],
                },
              ]);
              return null;
            }
          }

          // Normal file
          return Object.assign(file, {
            uuid: getId(),
            preview: URL.createObjectURL(file),
          });
        })
      );

      const validFiles = acceptedFiles.filter(Boolean) as UploadedFile[];

      if (!isDisabled) {
        let newFiles = replaceAfterUpload ? [] : [...fileUploaded];
        newFiles = [...newFiles, ...validFiles];

        const mappedRejections = fileRejections.map<RejectedFileWithPreview>(
          (item) => {
            const readableErrors = item.errors.map((err) =>
              getErrorText(err.code)
            );
            return {
              file: item.file,
              errors: item.errors.map((err) => ({ code: err.code, message: err.message })),
              preview: URL.createObjectURL(item.file),
              readableErrors,
            };
          }
        );

        setRejectedFile((prev) => [...prev, ...mappedRejections]);
        setFileUploaded(newFiles);
        return newFiles;
      }

      return fileUploaded;
    },
    validator: (item) => {
      const { name: fileName, type } = item;
      if (fileName) {
        const ext = fileName.split(".").pop()?.toLowerCase();
        if (
          accept &&
          accept?.[type] &&
          accept?.[type].length &&
          !accept?.[type].includes(`.${ext}`)
        ) {
          return {
            code: FILE_UPLOAD_ERROR.FILE_INVALID_TYPE,
            message: getErrorText(FILE_UPLOAD_ERROR.FILE_INVALID_TYPE),
          };
        }
      }
      return null;
    },
  });

  /** Remove a specific file */
  const removeFile = useCallback(
    (uuid: string) => {
      setFileUploaded((prev) => prev.filter((file) => file.uuid !== uuid));
    },
    [setFileUploaded]
  );

  /** Clear rejected files */
  const clearRejectedFiles = useCallback(() => {
    setRejectedFile([]);
  }, []);

  /** Cleanup memory leaks */
  useEffect(() => {
    return () => {
      fileUploaded.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      rejectedFiles.forEach((r) => r.preview && URL.revokeObjectURL(r.preview));
    };
  }, []);

  /** Return all handlers and states */
  return {
    getRootProps,
    getInputProps,
    isDragActive,
    isFileDialogOpen,
    rejectedFiles,
    openFileDialog: open,
    removeFile,
    clearRejectedFiles,
  };
};
